import { passengerNameVariants } from './passengerNames';

// Shared helpers for the Nuitee (LiteAPI) flight flow.
//
// Deliberately mirrors src/utils/hotelBooking.js: same quote-vs-book gate, same sector-patch
// shape, same saved-option round trip. Flights and hotels should behave identically from the
// trip's point of view — the only real differences are the API surface and the fact that a flight
// offer is a whole itinerary rather than a room.
//
// ⚠️ NEVER ADD A MARKUP HERE. Nuitee applies the account's commission (Connect → Flights →
// Commission: 2% rate search, 5% seats, 2% bags) to search results *before* they are returned —
// confirmed on the dashboard and by live test, where passing `margin: 0` and `margin: 25` both
// returned byte-identical prices. `pricing.display.total` is already the sell price. Marking it up
// is the same bug docs/LITEAPI_CUG_RATE_FIX.md records for commission hotel rates, and it would
// overcharge on every booking.

import { tripRequiresApproval } from './hotelBooking';

export { tripRequiresApproval };

// LiteAPI flight endpoints, all under the same base and API key as hotels.
export const FLIGHTS_BASE = 'https://api.liteapi.travel/v3.0';

// ⚠️ THE TRAILING SLASHES ARE NOT A TYPO AND ARE NOT CONSISTENT.
// rates/verify/prebooks must have NO trailing slash; bookings MUST have one. Get it wrong and the
// API answers 307 to the other form — and the redirect carries no Access-Control-Allow-Origin, so
// the browser blocks it and fetch rejects with a bare "Failed to fetch" that names nothing. Server
// -side tools follow the redirect happily, so this only ever breaks in the browser. Verified
// against the live API; see the table in CLAUDE.md.
export const FLIGHT_ENDPOINTS = {
  rates:    `${FLIGHTS_BASE}/flights/rates`,
  verify:   `${FLIGHTS_BASE}/flights/verify`,
  prebooks: `${FLIGHTS_BASE}/flights/prebooks`,
  bookings: `${FLIGHTS_BASE}/flights/bookings/`,
  // Ancillaries attach to an existing prebook, so this one is built per prebookId.
  services: (prebookId) => `${FLIGHTS_BASE}/flights/prebooks/${prebookId}/services`,
};

/**
 * Airport lookup. The rates endpoint takes IATA codes only — passing a city name returns
 * "required search field is missing", which reads like a bug in our form rather than a bad value.
 * The portal's own CITIES list is city names, so it cannot be used here; this resolves free text
 * to a real code instead.
 *
 * Returns [{ iata, city, country, name, hasAirlineService }], best match first. Deduped by IATA
 * because a city can return both an "All Airports" entry and the airport itself under one code,
 * and airports that actually have service are ranked first.
 */
export async function searchAirports(query, apiKey) {
  const q = (query || '').trim();
  if (q.length < 2) return [];

  // Returns null on a failed lookup, [] on a genuine no-match. The caller needs to tell those
  // apart: the endpoint rate-limits bursts (observed while testing), and showing "no airports
  // found" for a throttled request sends people hunting for a name that was always correct.
  const url = `${FLIGHTS_BASE}/data/flights/airports/?q=${encodeURIComponent(q)}`;
  const opts = { headers: { 'X-API-Key': apiKey, accept: 'application/json' } };

  let res;
  try { res = await fetch(url, opts); } catch { return null; }

  // One retry for throttling or a transient server error; anything else is a real failure.
  if (!res.ok && (res.status === 429 || res.status >= 500)) {
    await new Promise(r => setTimeout(r, 600));
    try { res = await fetch(url, opts); } catch { return null; }
  }
  if (!res.ok) return null;

  const json = await res.json().catch(() => null);
  const rows = (json?.data || []).flatMap(d => d.airports || []);

  // Collapse duplicate codes keeping the BEST row, not the first. A city can return an
  // "All Airports" metadata row carrying hasAirlineService:false under the same IATA as the real
  // airport — keeping the first row made "Melbourne" resolve to Avalon, 55km out of town, because
  // the serviceless MEL row was then sorted below AVV.
  const best = new Map();
  for (const a of rows) {
    if (!a?.iata) continue;
    const prev = best.get(a.iata);
    if (!prev || (!prev.hasAirlineService && a.hasAirlineService)) best.set(a.iata, a);
  }

  return [...best.values()]
    .sort((a, b) => {
      // Somewhere you can actually fly from beats a metadata-only entry.
      if (!!b.hasAirlineService !== !!a.hasAirlineService) return b.hasAirlineService ? 1 : -1;
      // Then an exact city-name hit — "Sydney" should offer SYD before Sydney, Nova Scotia.
      const exact = s => (s.city || '').toLowerCase() === q.toLowerCase();
      if (exact(a) !== exact(b)) return exact(a) ? -1 : 1;
      return 0;
    });
}

/** Already a bare IATA code? Then it needs no lookup. */
export function looksLikeIata(v) {
  return /^[A-Za-z]{3}$/.test((v || '').trim());
}

/** "Brisbane (BNE)" for display; the value sent to the API is always the code alone. */
export function airportLabel(a) {
  if (!a) return '';
  return `${a.city || a.name} (${a.iata})${a.country ? ` · ${a.country}` : ''}`;
}

export const CABIN_CLASSES = [
  { value: 'ECONOMY',         label: 'Economy' },
  { value: 'PREMIUM_ECONOMY', label: 'Premium Economy' },
  { value: 'BUSINESS',        label: 'Business' },
  { value: 'FIRST',           label: 'First' },
];

/**
 * 'book'  — run the full flow through prebook + payment
 * 'quote' — search and select only; the choice is saved to the trip for approval
 *
 * Same rule as hotels, and it applies to STX too: booking straight past a pending approval would
 * strand the request in the approver's queue and skip any policy-variance hold.
 */
export function flightFlowMode(tripStatus, requiresApproval) {
  if (['approved', 'booked'].includes(tripStatus)) return 'book';
  if (!requiresApproval) return 'book';
  return 'quote';
}

/** The sell price of an offer. No markup — see the warning at the top of this file. */
export function offerPrice(offer) {
  return parseFloat(offer?.pricing?.display?.total) || 0;
}

export function offerCurrency(offer) {
  return offer?.pricing?.display?.currency || 'AUD';
}

/**
 * Offers carry an `expiration` (~15 minutes from search, measured live). A stale offer must be
 * re-verified rather than booked, or the traveller is quoted a price the airline no longer holds.
 */
export function offerExpired(offer, now = Date.now()) {
  const exp = offer?.expiration ? Date.parse(offer.expiration) : NaN;
  return Number.isFinite(exp) ? exp <= now : false;
}

/** Minutes until an offer expires; negative once it has. */
export function minutesUntilExpiry(offer, now = Date.now()) {
  const exp = offer?.expiration ? Date.parse(offer.expiration) : NaN;
  return Number.isFinite(exp) ? Math.round((exp - now) / 60000) : null;
}

// ── Journey / segment summarising ─────────────────────────────────────────────

/** Segments belonging to one direction of a journey, in departure order. */
export function segmentsFor(journey, direction = 'OUTBOUND') {
  return (journey?.segments || [])
    .filter(s => !s.direction || s.direction === direction)
    .sort((a, b) => String(a.departureTime || '').localeCompare(String(b.departureTime || '')));
}

/** "QF" + "403" → "QF403". Falls back to whichever half is present. */
export function flightNumberOf(segment) {
  const code = segment?.carrier?.marketingCode || segment?.carrier?.operatingCode || '';
  const num  = segment?.flight?.marketingNumber || segment?.flight?.operatingNumber || '';
  return `${code}${num}`.trim();
}

export function airlineOf(segment) {
  return segment?.carrier?.marketingName || segment?.carrier?.operatingName || '';
}

/** Stops on a direction — one fewer than the number of segments. */
export function stopsFor(journey, direction = 'OUTBOUND') {
  return Math.max(0, segmentsFor(journey, direction).length - 1);
}

/** "2h 15m" from the duration objects the API returns. */
export function formatDuration(duration) {
  const mins = duration?.minutes;
  if (!Number.isFinite(mins)) return '';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

/** Local time portion of an ISO timestamp, without dragging in a date library. */
export function timeOf(iso) {
  if (!iso) return '';
  const m = String(iso).match(/T(\d{2}):(\d{2})/);
  return m ? `${m[1]}:${m[2]}` : '';
}

export function dateOf(iso) {
  return iso ? String(iso).slice(0, 10) : '';
}

/**
 * Baggage the fare already includes, as a short human string. Pieces priced at 0 are included;
 * anything with a price is a paid extra and belongs to the (later) ancillaries step, not here.
 */
export function includedBaggage(offer) {
  const inc = (offer?.baggage?.included || [])
    .filter(b => (parseFloat(b?.pricing?.display?.amount) || 0) === 0);
  if (inc.length === 0) return '';
  return inc.map(b => {
    const pieces = b.pieces > 1 ? `${b.pieces} × ` : '';
    const weight = b.weightKg ? ` ${b.weightKg}${b.unit || 'kg'}` : '';
    return `${pieces}${b.description || b.bagType}${weight}`.trim();
  }).join(', ');
}

/** Terms worth surfacing before someone commits — refundability and change rules. */
export function fareTerms(offer) {
  return {
    refundable: !!offer?.terms?.refundable,
    changeable: !!offer?.terms?.changeable,
    warnings:   (offer?.terms?.summary || []).filter(s => s.level === 'warning' || s.level === 'danger'),
  };
}

// ── Sector patches ────────────────────────────────────────────────────────────
//
// A journey may have several segments; a trip sector represents one flight. We write the
// itinerary's headline onto the sector (first departure → last arrival) and keep the full offer
// under proposedFlight/bookedFlight so nothing is lost.

function sectorHeadline(journey, direction) {
  const segs  = segmentsFor(journey, direction);
  const first = segs[0];
  const last  = segs[segs.length - 1];
  if (!first) return {};
  return {
    from:          first.originCode      || '',
    to:            last?.destinationCode || '',
    date:          dateOf(first.departureTime),
    departureTime: timeOf(first.departureTime),
    arrivalTime:   timeOf(last?.arrivalTime),
    airline:       airlineOf(first),
    // Multi-segment legs get the first marketing number; the rest live on the stored offer.
    flightNumber:  flightNumberOf(first),
  };
}

/**
 * A chosen-but-not-booked flight. `bookingRef` is deliberately NOT set — its absence alongside a
 * `proposedFlight` is what distinguishes a saved option from a confirmed booking, exactly as with
 * hotels. The price is mirrored onto the sector so the trip total, and therefore the approval
 * request, reflects the flight.
 */
export function sectorPatchFromFlightOption(sector, option) {
  return {
    ...sector,
    ...sectorHeadline(option.journey, option.direction),
    cabinClass:       option.cabinClass       || sector.cabinClass       || '',
    cost:             option.price != null ? String(option.price) : (sector.cost || ''),
    baggageAllowance: option.includedBaggage  || sector.baggageAllowance || '',
    international:    option.international ?? sector.international ?? false,
    proposedFlight:   option,
  };
}

/** A confirmed booking supersedes any saved option — clear it so the sector shows one state only. */
export function sectorPatchFromFlightBooking(sector, booking) {
  return {
    ...sector,
    ...sectorHeadline(booking.journey, booking.direction),
    cabinClass:       booking.cabinClass       || sector.cabinClass       || '',
    cost:             booking.price != null ? String(booking.price) : (sector.cost || ''),
    baggageAllowance: booking.includedBaggage  || sector.baggageAllowance || '',
    international:    booking.international ?? sector.international ?? false,
    // The airline PNR, not LiteAPI's own booking id — it is what an airline desk will ask for.
    bookingRef:       booking.bookingRef || sector.bookingRef || '',
    proposedFlight:   null,
    bookedFlight:     booking,
  };
}

/** Does this journey carry a return leg? */
export function hasReturnLeg(journey) {
  return (journey?.segments || []).some(s => s.direction === 'INBOUND');
}

/**
 * The sector fields for the return leg of a journey, or null for a one-way.
 *
 * A return is ONE fare covering both directions — the API prices it as a single total, not two
 * half-fares. Splitting it would invent numbers the airline never quoted, so the whole price stays
 * on the outbound sector and the return sector carries zero. The return still gets its own sector
 * because the flight genuinely happens: it has to appear in the itinerary and in the travel-day
 * and flight-number reports, which read sectors.
 */
export function returnSectorFrom(journey, option) {
  if (!hasReturnLeg(journey)) return null;
  const segs  = segmentsFor(journey, 'INBOUND');
  const first = segs[0];
  const last  = segs[segs.length - 1];
  if (!first) return null;

  return {
    type: 'flight',
    from:          first.originCode      || '',
    to:            last?.destinationCode || '',
    date:          dateOf(first.departureTime),
    departureTime: timeOf(first.departureTime),
    arrivalTime:   timeOf(last?.arrivalTime),
    airline:       airlineOf(first),
    flightNumber:  flightNumberOf(first),
    cabinClass:    option?.cabinClass || '',
    // Zero, deliberately — the fare is whole and sits on the outbound sector. A split would be
    // fiction and would not reconcile against the invoice.
    cost: '0',
    baggageAllowance: option?.includedBaggage || '',
    international: option?.international ?? false,
    // Bags and seats are bought per flight, so the return leg carries its own rather than having
    // them all pile up on the outbound where they would read as belonging to the wrong flight.
    purchasedBaggage: option?.purchasedBaggage || '',
    seatsSelected:    option?.seatsSelected    || '',
    notes: 'Return leg — fare is carried on the outbound sector of this booking.',
    // returnLegOf is stamped by the opener with the outbound sector's stable flightGroupId.
    // Never the offer id: those are regenerated by every search, so keying on one made a
    // re-priced booking insert a duplicate return sector instead of replacing the old one.
    returnLegOf: '',
  };
}

/**
 * The bags and seats bought for one flight sector.
 *
 * Two shapes to cope with: the OUTBOUND sector keeps the whole booking under `bookedFlight`,
 * while the return sector is built by returnSectorFrom and carries them flat. Trips booked before
 * extras were recorded per leg have them only on the outbound — still correct to show, just less
 * precise about which flight, which is why the labels now carry the route.
 */
export function sectorExtras(sector) {
  return {
    baggage: sector?.bookedFlight?.purchasedBaggage || sector?.purchasedBaggage || '',
    seats:   sector?.bookedFlight?.seatsSelected    || sector?.seatsSelected    || '',
  };
}

/** A saved option still awaiting booking — drives the "pending approval" UI on the sector. */
export function hasPendingFlightOption(sector) {
  return !!sector?.proposedFlight && !sector?.bookingRef;
}

/**
 * Trimmed payload handed back to the flow when re-opening a saved option, so it can restore the
 * search and compare the saved price against live offers. Kept small — it travels as a URL param.
 * Offer ids are stored for traceability only: they expire, so booking always re-queries live.
 */
export function encodeSavedFlight(option) {
  if (!option) return '';
  try {
    return JSON.stringify({
      origin:      option.origin,
      destination: option.destination,
      date:        option.date,
      returnDate:  option.returnDate,
      adults:      option.adults,
      children:    option.children,
      infants:     option.infants,
      cabinClass:  option.cabinClass,
      currency:    option.currency,
      price:       option.price,
      journeyKey:  option.journeyKey,
      offerId:     option.offerId,
      flightNumber: option.flightNumber,
      savedAt:     option.savedAt,
    });
  } catch { return ''; }
}

export function decodeSavedFlight(raw) {
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
}

// ── Identity documents from the passenger profile ─────────────────────────────
// Profiles store documents as { type, number, expiry, notes } where `type` comes from the client's
// configurable idTypes dropdown ("Passport", "Drivers Licence", …) and `notes` holds the issuing
// country as free text ("Australia / NSW"). The API wants documentType 'passport' | 'id' and an
// ISO-3166 alpha-2 issuing country, so neither field maps straight across.

const COUNTRY_CODES = {
  australia: 'AU', 'new zealand': 'NZ', 'united kingdom': 'GB', uk: 'GB', england: 'GB',
  scotland: 'GB', wales: 'GB', ireland: 'IE', 'united states': 'US', usa: 'US', america: 'US',
  canada: 'CA', india: 'IN', china: 'CN', japan: 'JP', singapore: 'SG', malaysia: 'MY',
  indonesia: 'ID', philippines: 'PH', thailand: 'TH', vietnam: 'VN', germany: 'DE',
  france: 'FR', italy: 'IT', spain: 'ES', netherlands: 'NL', 'south africa': 'ZA',
};

/** Free text like "Australia / NSW" → "AU". Empty when it can't be resolved — never a guess. */
export function issuingCountryCode(text) {
  const raw = (text || '').trim();
  if (!raw) return '';
  // A bare 2-letter code is already what we need.
  if (/^[A-Za-z]{2}$/.test(raw)) return raw.toUpperCase();
  // "Australia / NSW" — the country is the part before the separator.
  const head = raw.split(/[/,|]/)[0].trim().toLowerCase();
  return COUNTRY_CODES[head] || COUNTRY_CODES[raw.toLowerCase()] || '';
}

/** The only values /flights/prebooks accepts. Anything else is rejected outright. */
export const DOCUMENT_TYPES = ['passport', 'id'];

/** Profile document type → the API's 'passport' | 'id'. Anything that isn't a passport is 'id'. */
export function apiDocumentType(profileType) {
  return /passport/i.test(profileType || '') ? 'passport' : 'id';
}

/**
 * Last line of defence before the payload leaves. An invalid documentType is only discovered at
 * prebook — after the traveller has filled in every field and reached the confirm step — so it is
 * worth guaranteeing rather than trusting the form. 'id_card' shipped once and did exactly that.
 */
export function normalizeDocumentType(value) {
  const v = String(value || '').trim().toLowerCase();
  return DOCUMENT_TYPES.includes(v) ? v : apiDocumentType(v);
}

/** Is this document still valid on the travel date? Undated documents are treated as usable. */
export function documentValidOn(doc, isoDate) {
  if (!doc?.expiry || !isoDate) return true;
  return doc.expiry >= isoDate;
}

/**
 * The profile's documents in the order they should be offered for a flight, with the API shape
 * attached. Expired-by-the-travel-date documents sink to the bottom rather than disappearing —
 * a traveller renewing a passport still needs to see why the old one isn't being used.
 *
 * Passports rank first: they work on any route, whereas a licence is domestic-only.
 */
export function flightDocumentsFor(identityDocuments = [], travelDate = '') {
  return identityDocuments
    .filter(d => d && (d.number || '').trim())
    .map(d => ({
      label:   [d.type, d.number].filter(Boolean).join(' · '),
      valid:   documentValidOn(d, travelDate),
      type:    apiDocumentType(d.type),
      number:  (d.number || '').trim(),
      expiry:  d.expiry || '',
      country: issuingCountryCode(d.notes),
      sourceType: d.type || '',
    }))
    .sort((a, b) => {
      if (a.valid !== b.valid) return a.valid ? -1 : 1;
      if ((a.type === 'passport') !== (b.type === 'passport')) return a.type === 'passport' ? -1 : 1;
      return 0;
    });
}

// ── Ancillary services (bags now, seats later) ────────────────────────────────
//
// Shape confirmed against the live API, because the documentation is wrong in two places that
// matter: groups are `{ category, label, services }` — not `{ type, segmentKey, items }` — and
// `category` is lowercase 'baggage'/'seat', not 'BAGGAGE'/'SEAT'. `segmentKey` lives on each
// SERVICE, not on the group.
//
//   service = { serviceId, name, category, passengerType, segmentKey,
//               pricing: { display: { amount, currency } },
//               metadata: { baggage: { bagType, pieces, weightKg } | seat: {...} } }

/** Purchasable bags on a prebook, in the order the API listed them (grouped by leg). */
export function baggageOptions(prebookRow) {
  const groups = prebookRow?.servicesAttachable?.groups || [];
  return groups
    .filter(g => g.category === 'baggage')
    .flatMap(g => g.services || [])
    .filter(svc => svc?.serviceId);
}

/**
 * Bags grouped by the segment they apply to — they are sold per leg, so a MEL→SYD→AKL trip lists
 * each bag twice and buying one leg does not cover the other.
 */
export function baggageBySegment(prebookRow) {
  const out = new Map();
  for (const svc of baggageOptions(prebookRow)) {
    const key = svc.segmentKey || '';
    if (!out.has(key)) out.set(key, []);
    out.get(key).push(svc);
  }
  return [...out.entries()].map(([segmentKey, services]) => ({ segmentKey, services }));
}

/** A short label for a bag, preferring the API's own name. */
export function bagLabel(svc) {
  if (svc?.name) return svc.name;
  const m = svc?.metadata?.baggage || {};
  return [m.pieces > 1 ? `${m.pieces} ×` : '', m.bagType === 'cabin' ? 'Cabin bag' : 'Checked bag',
    m.weightKg ? `${m.weightKg}kg` : ''].filter(Boolean).join(' ');
}

export function servicePrice(svc) {
  return parseFloat(svc?.pricing?.display?.amount) || 0;
}

/**
 * The request body for attaching services.
 *
 * ⚠️ Every call REPLACES the entire selection — verified live: sending `[]` returns the price to
 * base, and sending two services in one call sums both. So always send the COMPLETE selection,
 * never a delta, or previously chosen bags are silently dropped.
 *
 * `passengerRef` is positional ('PAX1', 'PAX2', …) — the prebook's passenger objects carry no id
 * of their own. Note the published docs spell it `passergerRef`; that is a typo and is rejected.
 *
 * ⚠️ Each item carries its OWN ref, because bags and seats belong to one passenger: a support
 * worker's checked bag is not the traveller's. The ref is clamped against `passengerCount` —
 * the API accepts an out-of-range PAX and charges for it (see `clampPassengerRef`), so an off-by-one
 * here would take money and attach the bag to nobody.
 */
export function servicesPayload(selected = [], passengerCount = 1) {
  return {
    selectedServices: selected.map(svc => ({
      serviceId:  svc.serviceId,
      segmentKey: svc.segmentKey,
      passengerRef: clampPassengerRef(svc.passengerRef, passengerCount),
    })),
  };
}

/** What the bags add, for showing a running total before committing. */
export function servicesTotal(selected = []) {
  return selected.reduce((sum, svc) => sum + servicePrice(svc), 0);
}

// ── Frequent flyer / loyalty programmes ───────────────────────────────────────
//
// The API takes `loyaltyPrograms: [{ airlineCode, programNumber }]` on each prebook passenger, and
// **validates none of it** — verified live: 'ZZ', the literal string 'Qantas', and an empty
// programNumber were all accepted and echoed back verbatim. Whatever we send reaches the airline
// as-is, so the checking has to happen here.
//
// Passenger profiles store `loyaltyPrograms: [{ type, program, number }]` where `program` is free
// text ("Qantas Frequent Flyer", "Velocity"), so it needs resolving to an IATA code first.

/** Airline-type entries from a passenger profile, ignoring hotel/car/rail loyalty. */
export function airlineLoyaltyFromProfile(profileLoyalty = []) {
  return (profileLoyalty || [])
    .filter(lp => lp && /airline/i.test(lp.type || '') && (lp.number || '').trim())
    .map(lp => ({ program: (lp.program || '').trim(), number: (lp.number || '').trim() }));
}

/**
 * Resolve free text to an airline IATA code.
 *
 * ⚠️ The lookup matches a WORD PREFIX or an IATA code, and multi-word queries fail outright —
 * measured: "Virgin" resolves, "Virgin Australia International" does not; "NZ" resolves,
 * "Air New" does not. So a programme name is tried whole, then by its first word, which is what
 * turns "Qantas Frequent Flyer" into QF.
 *
 * Returns null rather than guessing when nothing matches — a wrong frequent flyer number on a
 * ticket credits someone else's account, and programme names like "Velocity" or "KrisFlyer" are
 * not airline names at all and never will resolve.
 */
export async function resolveAirlineCode(text, apiKey) {
  const raw = (text || '').trim();
  if (!raw) return null;
  if (/^[A-Za-z0-9]{2}$/.test(raw)) return raw.toUpperCase();

  const tries = [raw, raw.split(/\s+/)[0]].filter((v, i, a) => v && a.indexOf(v) === i);
  for (const q of tries) {
    let res;
    try {
      res = await fetch(`${FLIGHTS_BASE}/data/flights/airlines/?q=${encodeURIComponent(q)}`,
        { headers: { 'X-API-Key': apiKey, accept: 'application/json' } });
    } catch { return null; }
    if (!res.ok) continue;
    const json = await res.json().catch(() => null);
    const rows = (json?.data || []).flatMap(d => d.airlines || []);
    if (rows.length === 0) continue;
    // Prefer an exact name match, then an active carrier, then simply the first.
    const exact = rows.find(a => (a.name || '').toLowerCase() === q.toLowerCase());
    return (exact || rows.find(a => a.active) || rows[0]).iata || null;
  }
  return null;
}

/**
 * The entries whose SHAPE is sane enough to send.
 *
 * ⚠️ This is not verification and cannot be. Nothing anywhere checks that a membership number
 * belongs to the person travelling: the API accepts any value and echoes it back, and an airline
 * given a number that does not match the passenger normally just fails to credit the points —
 * silently, weeks later. `QF` + `9999999` passes every check that exists.
 *
 * All this does is stop obviously broken rows leaving the portal.
 */
export function validLoyaltyPrograms(entries = []) {
  return (entries || [])
    .map(e => ({
      airlineCode:   (e.airlineCode || '').trim().toUpperCase(),
      programNumber: (e.programNumber || '').trim(),
    }))
    .filter(e => /^[A-Z0-9]{2}$/.test(e.airlineCode) && e.programNumber.length > 0);
}

/**
 * Something visibly wrong with a membership number, or '' when it merely looks plausible.
 *
 * Deliberately generic. Per-airline formats exist — Qantas is seven digits, Velocity nine or ten —
 * but encoding rules I cannot verify would reject valid numbers and read as authority the portal
 * does not have. This only catches what is wrong on its face.
 */
export function loyaltyNumberWarning(number) {
  const n = (number || '').trim();
  if (!n) return '';
  if (/\s/.test(n))            return 'Contains a space — membership numbers do not.';
  if (n.length < 5)             return 'Looks too short for a membership number.';
  if (n.length > 20)            return 'Looks too long for a membership number.';
  if (!/^[A-Za-z0-9-]+$/.test(n)) return 'Contains characters membership numbers do not use.';
  return '';
}

// ── Seats ─────────────────────────────────────────────────────────────────────
//
// Shape measured live: metadata.seat = { available, position, seatColumn, seatNumber, seatRow,
// seatType }. seatType ∈ standard | extra_legroom | bulkhead | exit_row | window; position ∈
// window | middle | aisle. Roughly a quarter come back unavailable.
//
// ⚠️ The map is SPARSE — a SYD-MEL prebook returned 58 seats spread across rows 3–31, not a whole
// cabin. So this renders what the airline actually offered rather than drawing an aircraft, which
// would imply seats exist that were never returned. `position` is read from the API rather than
// inferred from the column letter: a 2-4-2 cabin would make any guess about where the aisle sits
// wrong.

export function seatOptions(prebookRow) {
  const groups = prebookRow?.servicesAttachable?.groups || [];
  return groups
    .filter(g => g.category === 'seat')
    .flatMap(g => g.services || [])
    .filter(svc => svc?.serviceId);
}

export function seatsBySegment(prebookRow) {
  const out = new Map();
  for (const svc of seatOptions(prebookRow)) {
    const key = svc.segmentKey || '';
    if (!out.has(key)) out.set(key, []);
    out.get(key).push(svc);
  }
  return [...out.entries()].map(([segmentKey, services]) => ({ segmentKey, services }));
}

/** Seats grouped into rows, ascending, each row's seats ordered by column. */
export function seatRows(services = []) {
  const rows = new Map();
  for (const svc of services) {
    const r = svc?.metadata?.seat?.seatRow;
    if (r == null) continue;
    if (!rows.has(r)) rows.set(r, []);
    rows.get(r).push(svc);
  }
  return [...rows.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([row, seats]) => ({
      row,
      seats: seats.sort((a, b) =>
        String(a.metadata?.seat?.seatColumn || '').localeCompare(String(b.metadata?.seat?.seatColumn || ''))),
    }));
}

export function seatAvailable(svc) {
  return svc?.metadata?.seat?.available === true;
}

export function seatNumber(svc) {
  const m = svc?.metadata?.seat || {};
  return m.seatNumber || `${m.seatRow ?? ''}${m.seatColumn ?? ''}`;
}

/** "Window · extra legroom" — the bits a traveller actually chooses on. */
export function seatDescription(svc) {
  const m = svc?.metadata?.seat || {};
  const type = { extra_legroom: 'extra legroom', exit_row: 'exit row', bulkhead: 'bulkhead' }[m.seatType];
  return [m.position, type].filter(Boolean).join(' · ');
}

/**
 * How a seat sits with a passenger who needs assistance.
 *
 *   'barred' — exit row. Regulation requires the occupant to operate the door and assist in an
 *              evacuation unaided, so anyone with an assistance SSR cannot sit there. Being moved
 *              at the gate is avoidable and humiliating.
 *   'check'  — extra legroom. Frequently IS the exit row and then carries the same restriction, but
 *              not always: some are bulkhead or front-of-cabin. Flagged to check rather than
 *              blocked, because wrongly barring a seat a traveller is entitled to is its own harm.
 *   ''       — no concern. Bulkhead is deliberately not flagged: it is often the seat a wheelchair
 *              user actually wants.
 */
export function seatAssistanceConcern(svc) {
  const type = svc?.metadata?.seat?.seatType;
  if (type === 'exit_row') return 'barred';
  if (type === 'extra_legroom') return 'check';
  return '';
}

// ── Schedule-order browsing, and leg-by-leg return selection ──────────────────
//
// Corporate travel is usually decided by time, not price — the meeting is at 10am — so results
// default to departure order with prices shown against each flight, the way an airline site works.
// Cheapest-first stays available as a toggle.
//
// ⚠️ Return journeys are PRE-PAIRED and the pairing is SPARSE. A BNE-SYD/SYD-BNE search returned
// 238 journeys from 26 outbound and 26 inbound flights — 676 if every combination existed. One
// outbound allowed 16 returns, another exactly 1. So choosing an outbound genuinely narrows the
// returns, and the count has to be shown up front rather than discovered after the choice.

export const SORT_MODES = [
  { value: 'time',  label: 'Departure time' },
  { value: 'price', label: 'Cheapest' },
];

/** Stable identity for one direction of a journey — the flights flown, in order. */
export function legKey(journey, direction = 'OUTBOUND') {
  return segmentsFor(journey, direction)
    .map(s => `${flightNumberOf(s)}@${s.departureTime}`)
    .join('+');
}

/**
 * Distinct flights for one direction, each carrying the journeys that contain it.
 *
 * `fromPrice` is the cheapest journey containing that flight, which is what "from $X" means on an
 * airline site: pick this flight and this is the least you could pay, depending on what you pair
 * it with. `pairCount` is how many onward options it leaves.
 */
export function groupByLeg(journeys = [], direction = 'OUTBOUND') {
  const groups = new Map();
  for (const j of journeys) {
    const k = legKey(j, direction);
    if (!k) continue;
    if (!groups.has(k)) {
      groups.set(k, { key: k, direction, segments: segmentsFor(j, direction), journeys: [] });
    }
    groups.get(k).journeys.push(j);
  }
  return [...groups.values()].map(g => ({
    ...g,
    pairCount: g.journeys.length,
    fromPrice: Math.min(...g.journeys.map(j => offerPrice(j.cheapestOffer || (j.offers || [])[0]))),
    departure: g.segments[0]?.departureTime || '',
  }));
}

/** Sort flights by when they leave, or by what they cost. */
export function sortLegGroups(groups = [], mode = 'time') {
  const out = [...groups];
  if (mode === 'price') out.sort((a, b) => a.fromPrice - b.fromPrice || a.departure.localeCompare(b.departure));
  else out.sort((a, b) => a.departure.localeCompare(b.departure) || a.fromPrice - b.fromPrice);
  return out;
}

/** Sort whole journeys the same way — used for one-way results, which need no pairing. */
export function sortJourneys(journeys = [], mode = 'time') {
  const dep = j => segmentsFor(j, 'OUTBOUND')[0]?.departureTime || '';
  const cost = j => offerPrice(j.cheapestOffer || (j.offers || [])[0]);
  const out = [...journeys];
  if (mode === 'price') out.sort((a, b) => cost(a) - cost(b) || dep(a).localeCompare(dep(b)));
  else out.sort((a, b) => dep(a).localeCompare(dep(b)) || cost(a) - cost(b));
  return out;
}

// ── Cancellation ──────────────────────────────────────────────────────────────
//
// ⚠️ THE SUCCESS PATH HAS NEVER BEEN EXERCISED. Measured in sandbox:
//   • bookings never leave `PENDING_CONFIRMATION`, even with `paymentStatus: 'completed'`;
//   • the quote 409s (code 49006, "cannot be quoted for cancellation in its current state")
//     on every booking, paid or not;
//   • `PUT .../cancel` returns **200 with an empty body and changes nothing** — status identical
//     before and after, and a second call also returns 200.
//
// So a 200 from the cancel endpoint proves nothing. Everything here is built to verify by
// re-reading the booking rather than trusting the response, because the alternative is telling
// someone their client's flight is cancelled when it is not.

export const CANCEL_ENDPOINTS = {
  quote:  (bookingId) => `${FLIGHTS_BASE}/flights/bookings/${bookingId}/cancellations`,
  cancel: (bookingId) => `${FLIGHTS_BASE}/flights/bookings/${bookingId}/cancel`,
  get:    (bookingId) => `${FLIGHTS_BASE}/flights/bookings/${bookingId}`,
};

/** Statuses that mean the airline is no longer holding the seat. */
const CANCELLED_STATUSES = new Set(['CANCELLED', 'CANCELED', 'VOIDED', 'REFUNDED']);

export function isCancelledStatus(status) {
  return CANCELLED_STATUSES.has(String(status || '').toUpperCase());
}

/** Pull the booking object out of whichever envelope the endpoint used. */
export function unwrapBooking(json) {
  const row = (json?.data || [])[0] || json || {};
  return row.booking || row;
}

/**
 * Normalise a cancellation quote.
 *
 * `confidence` matters and is surfaced verbatim: the API grades its own answer
 * confirmed | estimated | heuristic | unknown, and the refund is a *potential maximum* rather than
 * a promise. Presenting an "estimated" figure as final would have someone quote it to a client.
 */
export function parseCancelQuote(json) {
  const d = (json?.data || [])[0] || json || {};
  return {
    confidence:   d.confidence || 'unknown',
    isRefundable: d.isRefundable,
    isVoidable:   d.isVoidable,
    refund:       d.refund?.display  || null,
    penalty:      d.penalty?.display || null,
    penalties:    (d.penalties || []).map(p => ({
      type: p.type, description: p.description || '', amount: p.pricing?.display?.amount ?? null,
    })),
    destination:  d.destination || null,
    expiresAt:    d.expiresAt || null,
    pnr:          d.pnr || null,
  };
}

/** Plain-English gloss for the confidence grade, so nobody reads "heuristic" as "final". */
export function confidenceNote(confidence) {
  return {
    confirmed: 'Confirmed by the airline.',
    estimated: 'Estimated from the fare rules — the airline may settle on a different figure.',
    heuristic: 'A rough estimate only. Treat it as indicative and confirm with the airline.',
    unknown:   'The airline gave no refund detail. Confirm the outcome with them directly.',
  }[confidence] || 'Confirm the outcome with the airline directly.';
}

// ── More than one passenger on a booking ──────────────────────────────────────
//
// Supported travel usually means two people on the aircraft: the traveller and a support worker or
// carer. The trip already records that — `additionalPassengers[]` carries a `sectorIndices` list
// saying which sectors each person is actually on, since a support worker may join for only part
// of a trip.
//
// ⚠️ `passengerRef` IS NOT VALIDATED BY THE API. Verified live on a two-passenger prebook: a bag
// attached to 'PAX1', 'PAX2' and 'PAX3' each returned 200 and each added the same fare — including
// PAX3, who did not exist. The reference is positional against the prebook's `passengers[]` array
// and nothing checks it is in range, so a bad ref takes the money and attaches the bag to nobody.
// `passengerRefFor` plus `clampPassengerRef` keep it honest on this side; nothing downstream will.

/** Positional reference for the nth prebook passenger. */
export function passengerRefFor(index) {
  return `PAX${(Number(index) || 0) + 1}`;
}

/** A ref that actually addresses one of `count` passengers, or PAX1 when it does not. */
export function clampPassengerRef(ref, count) {
  const n = parseInt(String(ref || '').replace(/^PAX/i, ''), 10);
  return Number.isFinite(n) && n >= 1 && n <= Math.max(1, count) ? `PAX${n}` : 'PAX1';
}

const paxFullName = (p) =>
  [p?.firstName, p?.lastName].filter(Boolean).join(' ').trim() || (p?.name || '').trim();

const normaliseName = (s) => String(s || '').trim().toLowerCase().replace(/\s+/g, ' ');

/**
 * Which people on a completed booking are not already known to the trip.
 *
 * ⚠️ Matched on PROFILE ID and on every accepted spelling of a name — never on the ticketed name
 * alone. A trip's `travellerName` is written from the profile's PREFERRED name while the airline
 * is always given the LEGAL one, so "Kate Smith" and "Katherine Jane Smith" are routinely the same
 * person. Plain equality added the traveller to their own trip a second time as an additional
 * passenger, which then flowed into cost allocation and the support worker report.
 *
 * Callers map the survivors into their own row shape — TripForm tracks sectors by key and
 * TripDetail by index, so the row cannot be built here.
 */
export function newBookingPassengers(booked, {
  travellerName = '', travellerProfile = null, additionalPassengers = [], profiles = [],
} = {}) {
  const byId = new Map((profiles || []).filter(p => p?.id).map(p => [p.id, p]));

  const knownIds = new Set([
    travellerProfile?.id,
    ...(additionalPassengers || []).map(ap => ap?.passengerId),
  ].filter(Boolean));

  // Every spelling that already refers to somebody on this trip.
  const knownNames = new Set();
  const remember = (name, profile) => {
    const n = normaliseName(name);
    if (n) knownNames.add(n);
    if (profile) passengerNameVariants(profile).forEach(v => knownNames.add(v));
  };
  remember(travellerName, travellerProfile);
  for (const ap of additionalPassengers || []) {
    remember(ap?.name, ap?.passengerId ? byId.get(ap.passengerId) : null);
  }

  return (booked || []).filter((p) => {
    if (!p?.name) return false;
    if (p.passengerId && knownIds.has(p.passengerId)) return false;
    if (knownNames.has(normaliseName(p.name))) return false;
    // The booked person may hold a profile the trip refers to by a different spelling again.
    const profile = p.passengerId ? byId.get(p.passengerId) : null;
    if (profile && passengerNameVariants(profile).some(v => knownNames.has(v))) return false;
    return true;
  });
}

/**
 * Everyone travelling on one flight sector: the trip's traveller first, then any additional
 * passenger whose `sectorIndices` includes this sector.
 *
 * An additional passenger with NO `sectorIndices` is treated as being on every sector — that is
 * how a trip saved before per-sector allocation existed reads, and dropping those people from a
 * booking would be worse than including someone who can be removed by hand.
 *
 * Each entry carries the passenger profile when one can be found, so names, documents and frequent
 * flyer numbers pre-fill. A profile is optional: guests and one-off carers often have none, and the
 * booking must still be possible.
 */
export function flightPassengersFor(trip, sectorIndex, passengers = [], primaryProfile = null) {
  const byId   = new Map((passengers || []).map(p => [p.id, p]));
  // Keyed on every accepted spelling, not just the legal one: a trip names people by their
  // preferred name, so a legal-name-only map misses exactly the profiles this needs to find.
  const byName = new Map();
  for (const p of passengers || []) {
    for (const variant of [...passengerNameVariants(p), normaliseName(paxFullName(p))]) {
      if (variant && !byName.has(variant)) byName.set(variant, p);
    }
  }

  const out = [{
    name:      (trip?.travellerName || '').trim(),
    role:      'traveller',
    isPrimary: true,
    profile:   primaryProfile
      || byName.get(normaliseName(trip?.travellerName))
      || null,
  }];

  for (const ap of (trip?.additionalPassengers || [])) {
    const name = (ap?.name || '').trim();
    if (!name) continue;
    const onThisSector = !Array.isArray(ap.sectorIndices) || ap.sectorIndices.length === 0
      ? true
      : ap.sectorIndices.includes(sectorIndex);
    if (!onThisSector) continue;
    out.push({
      name,
      role:      ap.role || 'traveller',
      isPrimary: false,
      profile:   byId.get(ap.passengerId) || byName.get(normaliseName(name)) || null,
    });
  }
  return out;
}

/** The subset of a passenger profile the booking flow needs, small enough to ride on a URL. */
export function flightPassengerParam(entry) {
  const p = entry?.profile || {};
  return {
    name: entry?.name || '',
    role: entry?.role || 'traveller',
    firstName:  p.firstName  || '',
    lastName:   p.lastName   || '',
    middleName: p.middleName || '',
    dateOfBirth: p.dateOfBirth || '',
    gender: p.gender || '',
    email:  p.email  || '',
    phone:  p.phone  || '',
    loyaltyPrograms: p.loyaltyPrograms || [],
    identityDocuments: (p.identityDocuments || [])
      .filter(d => d && (d.number || '').trim())
      .map(d => ({ type: d.type, number: d.number, expiry: d.expiry, notes: d.notes })),
  };
}

/** Role label for the booking UI — says why a second person is on the ticket. */
export function passengerRoleLabel(role) {
  return { support_worker: 'Support worker', carer: 'Carer', traveller: 'Traveller' }[role] || 'Traveller';
}
